import { useEffect, useState, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { departments } from "@/mocks/departments";
import { useAuth } from "@/hooks/useAuth";
import { fetchLiveGroups } from "@/services/zammadService";
import { HEADER_LOGO_IMAGE } from "@/utils/images";

export default function BIDNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [deptOpen, setDeptOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const deptRef = useRef<HTMLDivElement>(null);
  
  const { user } = useAuth();
  const [navDepts, setNavDepts] = useState<{ id: string; name: string; icon: string; shortName: string }[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (deptRef.current && !deptRef.current.contains(e.target as Node)) {
        setDeptOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Standardize Zammad group name to clean URL slug directly from name/ID
  const getSlugForGroupName = (name: string) => {
    const lower = (name || "").toLowerCase().trim();
    if (lower.includes("servicio de ti") || lower === "ti") return "ti";
    if (lower.includes("administracion")) return "administracion";
    if (lower.includes("servicios generales")) return "servicios-generales";
    if (lower.includes("talento humano")) return "talento-humano";
    if (lower.includes("legal")) return "legal";
    if (lower.includes("contraloria")) return "contraloria";
    if (lower.includes("diseno") || lower.includes("marketing")) return "diseno-marketing";

    return lower
      .replace(/[áéíóúü]/g, (match) => {
        const map: Record<string, string> = { á: "a", é: "e", í: "i", ó: "o", ú: "u", ü: "u" };
        return map[match] || match;
      })
      .replace(/ñ/g, "n")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  // Fetch groups dynamically for the logged-in user in navbar
  // Strategy: stale-while-revalidate — show cached data instantly, refresh in background
  useEffect(() => {
    const cacheKey = user ? `zammad_permitted_microsites_${user.id}` : "zammad_permitted_microsites_public";

    // Helper to resolve groups from API response
    function resolveGroups(res: { categoryTree?: any[]; groups?: any[] }) {
      const rootCategories = res.categoryTree || [];
      if (rootCategories.length === 0 && res.groups && res.groups.length > 0) {
        const rootGroupNames = Array.from(new Set(res.groups.map((g: any) => g.name.split("::")[0])));
        return rootGroupNames.map((name) => {
          const slug = getSlugForGroupName(name);
          const staticInfo = departments.find((d) => d.id === slug);
          return {
            id: slug,
            name: name,
            icon: staticInfo?.icon || "ri-organization-chart",
            shortName: staticInfo?.shortName || name.substring(0, 3).toUpperCase()
          };
        });
      }

      return rootCategories.map((c: any) => {
        const slug = getSlugForGroupName(c.name);
        const staticInfo = departments.find((d) => d.id === slug);
        return {
          id: slug,
          name: c.name,
          icon: staticInfo?.icon || "ri-organization-chart",
          shortName: staticInfo?.shortName || c.name.substring(0, 3).toUpperCase()
        };
      });
    }

    // 1. Show cached data immediately (no loading flicker)
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        setNavDepts(JSON.parse(cached));
      } catch (_) {
        // Ignore stale or malformed cache entries and revalidate below.
      }
    }

    // 2. Refresh from Zammad in background
    let active = true;
    async function revalidate() {
      // Only show loading spinner if there's nothing cached yet
      if (!cached) setLoading(true);
      try {
        const res = await fetchLiveGroups(user ? { userId: user.id, email: user.email } : undefined);
        if (!active) return;
        if (res.error) return;
        const resolved = resolveGroups(res);
        if (resolved) {
          const fresh = JSON.stringify(resolved);
          // Only update state + cache if data actually changed
          if (fresh !== cached) {
            setNavDepts(resolved);
            sessionStorage.setItem(cacheKey, fresh);
          }
        }
      } catch (err) {
        console.error("Failed to load nav groups:", err);
      } finally {
        if (active) setLoading(false);
      }
    }
    revalidate();
    return () => {
      active = false;
    };
  }, [user]);

  // Detect current microsite
  const currentPath = location.pathname;
  const currentDept = navDepts.find((d) => currentPath.startsWith(`/${d.id}`));

  const isHub = currentPath === "/";
  const navigateTo = (path: string) => {
    setDeptOpen(false);
    setMobileOpen(false);
    navigate(path);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 border-b border-background-200/70 bg-white/95 backdrop-blur-lg transition-shadow duration-300 ${
        scrolled ? "shadow-[0_8px_30px_rgba(0,28,113,0.08)]" : "shadow-sm"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-14">
        <div className="flex h-20 items-center justify-between md:h-24">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); navigateTo("/"); }}
            className="group flex flex-shrink-0 cursor-pointer items-center"
          >
            <img
              src={HEADER_LOGO_IMAGE}
              alt="BID Group"
              className="h-auto max-h-12 w-[150px] object-contain transition-transform duration-300 group-hover:scale-[1.02] md:max-h-16 md:w-[190px]"
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); navigateTo("/"); }}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                isHub ? "text-primary-500 bg-primary-50" : "text-foreground-700 hover:text-primary-500 hover:bg-primary-50"
              }`}
            >
              <i className="ri-home-3-line mr-1.5 text-sm"></i>
              Inicio
            </a>

            {/* Departments Dropdown */}
            {navDepts.length > 0 && (
              <div ref={deptRef} className="relative">
                <button
                  onClick={() => setDeptOpen(!deptOpen)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 flex items-center gap-1 ${
                    currentDept ? "text-primary-500 bg-primary-50" : "text-foreground-700 hover:text-primary-500 hover:bg-primary-50"
                  }`}
                >
                  <i className="ri-grid-line mr-1 text-sm"></i>
                  Micrositios
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${deptOpen ? "rotate-180" : ""}`}></i>
                </button>

                {deptOpen && (
                  <div className="absolute top-full mt-2 left-0 w-72 rounded-xl bg-background-50 border border-background-200 shadow-lg py-2 overflow-hidden">
                    {navDepts.map((dept) => (
                      <button
                        key={dept.id}
                        onClick={() => navigateTo(`/${dept.id}`)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer ${
                          currentDept?.id === dept.id
                            ? "bg-primary-50 text-primary-700"
                            : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                        }`}
                      >
                        <span className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                          <i className={`${dept.icon} text-sm text-foreground-600`}></i>
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{dept.name}</div>
                          <div className="text-xs text-foreground-400 truncate">{dept.shortName}</div>
                        </div>
                        {currentDept?.id === dept.id && (
                          <i className="ri-check-line text-primary-500 ml-auto"></i>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Current microsite CTA */}
            {currentDept && (
              <button
                onClick={() => {
                  setDeptOpen(false);
                  navigate(`/${currentDept.id}?modal=general`);
                }}
                className="ml-2 px-5 py-2 rounded-full bg-primary-500 text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all duration-200"
              >
                Solicitar Servicio
              </button>
            )}

            {/* Mis Tickets */}
            <a
              href="/mis-tickets"
              onClick={(e) => { e.preventDefault(); navigateTo("/mis-tickets"); }}
              className={`ml-1 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  currentPath === "/mis-tickets" ? "text-primary-500 bg-primary-50" : "text-foreground-700 hover:text-primary-500 hover:bg-primary-50"
              }`}
            >
              <i className="ri-user-3-line text-sm"></i>
              Mis tickets
            </a>

            {/* Estadísticas */}
            <a
              href="/estadisticas"
              onClick={(e) => { e.preventDefault(); navigateTo("/estadisticas"); }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  currentPath === "/estadisticas" ? "text-primary-500 bg-primary-50" : "text-foreground-700 hover:text-primary-500 hover:bg-primary-50"
              }`}
            >
              <i className="ri-bar-chart-grouped-line text-sm"></i>
              Estadísticas
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="Menú"
          >
            <i
              className={`text-xl ${
                "text-primary-500"
              } ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}
            ></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          mobileOpen ? "max-h-[700px] opacity-100" : "max-h-0 opacity-0"
        } bg-background-50/98 backdrop-blur-md border-b border-background-200/70`}
      >
        <div className="px-4 py-4 flex flex-col gap-1">
          <button
            onClick={() => navigateTo("/")}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors cursor-pointer ${
              isHub ? "text-primary-700 bg-primary-50" : "text-foreground-700 hover:bg-background-100"
            }`}
          >
            <i className="ri-home-3-line text-lg"></i>
            <span className="font-medium text-sm">Inicio</span>
          </button>

          {navDepts.length > 0 && (
            <>
              <div className="h-px bg-background-200 my-2"></div>

              <p className="px-4 text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-1">
                Micrositios
              </p>

              {navDepts.map((dept) => (
                <button
                  key={dept.id}
                  onClick={() => navigateTo(`/${dept.id}`)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors cursor-pointer ${
                    currentDept?.id === dept.id
                      ? "text-primary-700 bg-primary-50"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  <i className={`${dept.icon} text-lg`}></i>
                  <span className="font-medium text-sm">{dept.name}</span>
                  {currentDept?.id === dept.id && (
                    <span className="ml-auto w-2 h-2 rounded-full bg-primary-500"></span>
                  )}
                </button>
              ))}
            </>
          )}

          {currentDept && (
            <button
              onClick={() => {
                setMobileOpen(false);
                navigate(`/${currentDept.id}?modal=general`);
              }}
              className="mt-3 mx-2 mb-2 px-5 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold text-center cursor-pointer hover:bg-primary-600 transition-all"
            >
              Solicitar Servicio
            </button>
          )}

          <button
            onClick={() => navigateTo("/mis-tickets")}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors cursor-pointer ${
              currentPath === "/mis-tickets" ? "text-primary-700 bg-primary-50" : "text-foreground-700 hover:bg-background-100"
            }`}
          >
            <i className="ri-user-3-line text-lg"></i>
            <span className="font-medium text-sm">Mis tickets</span>
          </button>

          <button
            onClick={() => navigateTo("/estadisticas")}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors cursor-pointer ${
              currentPath === "/estadisticas" ? "text-primary-700 bg-primary-50" : "text-foreground-700 hover:bg-background-100"
            }`}
          >
            <i className="ri-bar-chart-grouped-line text-lg"></i>
            <span className="font-medium text-sm">Estadísticas</span>
          </button>
        </div>
      </div>
    </nav>
  );
}
